import axios from 'axios'
import { useCallback, useEffect, useState } from 'react'
import { getFeed, saveStatistic } from '@/api/api'
import {
  FEED_PER_PAGE,
  FeedPageParams,
  FeedPostNotification
} from '@/feed/Feed.interface'
import Feed from '@/feed/Feed'
import Loading from '@/components/common/Loading'
import FailedToFetch from '@/components/error/FailedToFetch'
import BrandedToolbar, {
  isToolbarHidden
} from '@/components/Toolbar/BrandedToolbar'
import useBranding from '@/lib/useBranding'
import usePageParams from '@/lib/usePageParams'

function logFeedError(error: unknown, alias: string) {
  if (axios.isAxiosError(error)) {
    const headers = { ...error.config?.headers }
    // Remove sensitive headers before logging
    if (headers['x-key']) {
      headers['x-key'] = '[REDACTED]'
    }
    // eslint-disable-next-line no-console
    console.error('Feed fetch error:', {
      message: error.message,
      status: error.response?.status,
      statusText: error.response?.statusText,
      data: error.response?.data,
      url: error.config?.url,
      method: error.config?.method,
      headers,
      requestParams: {
        alias,
        page: 1,
        perPage: FEED_PER_PAGE
      }
    })
  } else {
    // eslint-disable-next-line no-console
    console.error('Feed fetch error:', error)
  }
}

function readInboxItems(payload: unknown): FeedPostNotification[] {
  // Support both camelCase (axios-case-converter) and snake_case (raw API) response
  const data = payload as {
    inboxItems?: FeedPostNotification[]
    inbox_items?: FeedPostNotification[]
  }

  if (Array.isArray(data?.inboxItems)) {
    return data.inboxItems
  }
  if (Array.isArray(data?.inbox_items)) {
    return data.inbox_items
  }
  return []
}

export default function FeedRoute() {
  const pageParams: FeedPageParams = usePageParams()
  const { branding, isLoading: isBrandingLoading } = useBranding(pageParams)

  const [feedFirstPage, setFeedFirstPage] = useState<FeedPostNotification[]>([])
  const [isFeedLoading, setIsFeedLoading] = useState(true)
  const [hasServerError, setHasServerError] = useState(false)

  const { alias, sdkAppId, sdkAppKey } = pageParams
  const key = `${sdkAppId}${sdkAppKey}`

  useEffect(() => {
    let cancelled = false
    setIsFeedLoading(true)
    setHasServerError(false)

    const loadFirstPage = async () => {
      try {
        const response = await getFeed({
          params: {
            alias,
            page: 1,
            perPage: FEED_PER_PAGE
          },
          key,
          additionalHeaders: {
            source: 'native'
          }
        })

        if (!cancelled) {
          setFeedFirstPage(readInboxItems(response.data))
        }
      } catch (error: unknown) {
        logFeedError(error, alias)
        if (!cancelled) {
          setHasServerError(true)
        }
      } finally {
        if (!cancelled) {
          setIsFeedLoading(false)
        }
      }
    }

    loadFirstPage()

    return () => {
      cancelled = true
    }
  }, [alias, key])

  const sendClickStat = useCallback(
    async ({
      buttonNumber,
      campaignGuid
    }: {
      campaignGuid: string
      buttonNumber: number
    }): Promise<void> => {
      try {
        await saveStatistic({
          key,
          data: {
            campaignGuid,
            alias,
            key:
              buttonNumber === 1
                ? 'card_front_button_click_one'
                : 'card_front_button_click_two'
          }
        })
      } catch (error: unknown) {
        // eslint-disable-next-line no-console
        console.error(error)
      }
    },
    [alias, key]
  )

  const sendDeleteFeedpostStat = useCallback(
    async ({ campaignGuid }: { campaignGuid: string }): Promise<void> => {
      try {
        await saveStatistic({
          key,
          data: {
            campaignGuid,
            alias,
            key: 'card_delete'
          }
        })
      } catch (error: unknown) {
        // eslint-disable-next-line no-console
        console.error(error)
      }
    },
    [alias, key]
  )

  if (isBrandingLoading || isFeedLoading) {
    return <Loading />
  }

  if (hasServerError) {
    return (
      <>
        <BrandedToolbar branding={branding} />
        <div className={`${isToolbarHidden(branding) ? 'pt-0' : 'pt-[55px]'}`}>
          <FailedToFetch text="Failed to load feed" />
        </div>
      </>
    )
  }

  return (
    <Feed
      branding={branding}
      sendClickStat={sendClickStat}
      sendDeleteFeedpostStat={sendDeleteFeedpostStat}
      hasNextPage={feedFirstPage.length === FEED_PER_PAGE}
      feedFirstPage={feedFirstPage}
      pageParams={pageParams}
    />
  )
}
